import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type { StorageArea } from '../services/BrowserStorage';
import {
    parseStoredValue,
    readFromStorage,
    readRawFromStorage,
    subscribeToStorage,
    writeToStorage,
} from '../services/BrowserStorage';

type Updater<T> = T | ((prev: T) => T);

function isUpdaterFn<T>(value: Updater<T>): value is (prev: T) => T {
    return typeof value === 'function';
}

const NOTHING_STORED = Symbol('nothing stored');

export function useBrowserStorage<T>(
    storageKey: string,
    init: T,
    area: StorageArea
): [T, (updater: Updater<T>) => void] {
    const subscribe = useCallback(
        (onChange: () => void) => subscribeToStorage(area, storageKey, onChange),
        [area, storageKey]
    );
    // The raw string is the snapshot: it compares by value, so React re-renders exactly when the stored value
    // changes — and reads the new key's value in the same render when the key changes.
    const raw = useSyncExternalStore(subscribe, () => readRawFromStorage(area, storageKey));
    const stored = useMemo(() => parseStoredValue<T | typeof NOTHING_STORED>(raw, NOTHING_STORED), [raw]);

    // Callers often pass `init` as an inline literal. Keying it by value keeps the default the same object for as
    // long as it means the same thing, just like a stored value.
    const initJson = JSON.stringify(init) as string | undefined;
    const fallback = useMemo(() => (initJson === undefined ? undefined : JSON.parse(initJson)) as T, [initJson]);

    const replace = useCallback(
        (updater: Updater<T>) => {
            const prevValue = readFromStorage<T>(area, storageKey, init);
            const nextValue = isUpdaterFn(updater) ? updater(prevValue) : updater;
            writeToStorage(area, storageKey, nextValue);
        },
        [area, storageKey, init]
    );

    return [stored === NOTHING_STORED ? fallback : stored, replace];
}
